// Sync's arithmetic (P3.9) — FR-07, AC-2's "engaging Sync produces no audible phase flam
// (alignment within ±10 ms)".
//
// **Pure, and that is the point.** P3.9's rule puts the phase correction on the audio thread, where
// nothing can be tested: no `expect`, no fixtures, no way to run it twice. So the audio thread gets
// the plumbing and this gets the decisions — every function here is a number in and a number out,
// and `sync.test.ts` drives them across the corpus's whole tempo range in Node.
//
// ## Why this is in /platform/types and not /audio/dsp
//
// It was written in `/audio/dsp/sync.ts` first, and `ui-no-engine-internals` refused the build:
// **the UI needs half of this** — the tempo match moves the pitch fader, which is a UI action — and
// that rule exists so "DSP and worklet code [does not get] dragged into the app bundle". Routing it
// through the `/audio/engine` barrel would have satisfied the letter and defeated the purpose, since
// the code lands in the bundle either way.
//
// The rule was right and the file was in the wrong place. None of this is signal processing; it is
// arithmetic about beats, shared by the worklet that applies it and the UI that asks for it — the
// same shape as `transport.ts`'s state machine, which lives here for the same reason and is imported
// by both sides. `platform-is-a-leaf` lets everyone reach it and it reaches nothing back.
//
// ## The two halves, and why they are separate functions
//
// P3.9: "Tempo match is a rate change through P3.6's existing path; phase alignment is a one-shot
// correction, and the two must be separable because a DJ drops one without the other."
//
//   * **Tempo** is a ratio of beat periods. It is a *setting* — it persists, it is visible on the
//     pitch fader, and it survives sync being dropped.
//   * **Phase** is where in the bar each deck is. It is an *event* — corrected once, and then held
//     only because the tempos match. Nothing maintains it.
//
// The order is forced by that second sentence: **match tempo first.** Once the effective tempos are
// equal the phase difference is constant, so a correction stays correct. Correct phase first and the
// two decks walk apart again at the rate of their tempo difference — at 128 against 130 BPM, a full
// beat of drift in about thirty seconds.

/** What a deck's beatgrid is, for sync's purposes: a first beat and a spacing. */
export interface SyncGrid {
  /** Seconds from the start of the track to the first beat. */
  readonly anchorSeconds: number;
  /** Seconds between beats. */
  readonly beatSeconds: number;
}

/** Is this grid usable for sync at all? A zero or negative period divides by zero downstream. */
export function isSyncableGrid(grid: SyncGrid | null): grid is SyncGrid {
  return (
    grid !== null &&
    Number.isFinite(grid.anchorSeconds) &&
    Number.isFinite(grid.beatSeconds) &&
    grid.beatSeconds > 0
  );
}

/**
 * How far into the current beat a position is, as a fraction of a beat in `[0, 1)`.
 *
 * Works for positions before the anchor, which is not a nicety: a grid's anchor is the first beat
 * *detected*, and tracks routinely have audio before it. JavaScript's `%` keeps the sign of the
 * dividend, so a naive modulo returns a negative phase there and every comparison downstream
 * silently inverts.
 */
export function beatPhase(positionSeconds: number, grid: SyncGrid): number {
  const beats = (positionSeconds - grid.anchorSeconds) / grid.beatSeconds;
  const phase = beats - Math.floor(beats);
  // `Math.floor` already forces the result into [0, 1), but a position far enough from the anchor
  // loses enough precision that `beats - floor(beats)` can round to exactly 1. Guarding here is
  // cheaper than every caller wondering.
  return phase >= 1 ? 0 : phase;
}

/**
 * Seconds to add to `selfPosition` so this deck's beat phase matches the other's.
 *
 * **Signed, and wrapped to at most half a beat in either direction.** A DJ hitting sync expects the
 * nearest beat, not the next one: at 128 BPM the difference is jumping 12 ms rather than 457 ms, and
 * the second would be audible as a stumble rather than an alignment.
 *
 * The result is in **this deck's track seconds**, because that is what a playhead is measured in.
 * Phase is dimensionless — a fraction of a beat — so converting it back uses *this* deck's beat
 * period even when the other deck's differs.
 *
 * Returns 0 for an unusable grid on either side rather than throwing. This is called from the audio
 * thread, where a throw kills the whole graph, and "do not move" is the right answer for a deck that
 * has nothing to align to.
 */
export function phaseCorrectionSeconds(
  selfPositionSeconds: number,
  selfGrid: SyncGrid | null,
  otherPositionSeconds: number,
  otherGrid: SyncGrid | null,
): number {
  if (!isSyncableGrid(selfGrid) || !isSyncableGrid(otherGrid)) {
    return 0;
  }

  const difference =
    beatPhase(otherPositionSeconds, otherGrid) - beatPhase(selfPositionSeconds, selfGrid);
  // Wrap into [-0.5, 0.5): `difference` is in (-1, 1), so one conditional pair does it without a
  // modulo. −0.5 exactly is left alone; it is half a beat either way and the sign is arbitrary.
  const wrapped =
    difference > 0.5 ? difference - 1 : difference < -0.5 ? difference + 1 : difference;
  return wrapped * selfGrid.beatSeconds;
}

/**
 * Where a phase correction should actually land, kept inside the track by **whole beats** (P4.17).
 *
 * ## The bug this replaces, and why the clamp looked right
 *
 * `phaseCorrectionSeconds` is signed and wraps to at most half a beat, so a Sync pressed shortly
 * after a deck starts can ask it to move *backwards past frame 0* — the nearest matching beat is
 * simply not in the track yet. The worklet clamped the result into the track, which is what the
 * transport does everywhere else and reads as obviously correct:
 *
 *     this.#position = Math.min(lastFrame, Math.max(0, moved));
 *
 * ⚠️ **A clamped correction is a correction that did not happen**, and sync is one-shot: nothing
 * retries, so the un-applied remainder becomes a permanent phase error. Measured across the Sync
 * end-to-end test on 11–13 September: residuals of 18.9, 21.4, 31.4, 56.4 and 58.9 ms against
 * AC-2's ±10 ms, every one of them inside half a beat, on runs where the audio thread was
 * demonstrably healthy (3.18 ms of clock drift on the failure that was measured properly).
 *
 * ## Why whole beats are the right move rather than a bigger clamp
 *
 * **Beat phase is periodic.** A deck one beat later is aligned exactly as well as a deck on the
 * beat before it — that is what phase *means*, and it is why `phaseCorrectionSeconds` is free to
 * pick the nearest beat in the first place. So when the nearest beat lies outside the track, the
 * next one along is the same alignment and is inside it.
 *
 * ⚠️ **This aligns beats, not bars.** A whole-beat shift is invisible to phase and would be audible
 * to a listener counting bars, which SYNAPSE does not currently track — FR-07 is a beat-phase claim.
 * A bar-aware sync would have to shift by whole *bars* here instead, and this comment is the note
 * that says so rather than the bug report that finds out.
 *
 * Falls back to a clamp when no whole-beat shift can help: a track shorter than one beat has nowhere
 * to put the playhead, and an unusable beat length would divide by zero. Both return the closest
 * legal position, which is the old behaviour and the right answer when the new one is impossible.
 */
export function alignInsideTrack(
  targetSeconds: number,
  beatSeconds: number,
  durationSeconds: number,
): number {
  const clamp = (value: number): number => Math.min(durationSeconds, Math.max(0, value));

  if (
    !Number.isFinite(targetSeconds) ||
    !Number.isFinite(beatSeconds) ||
    beatSeconds <= 0 ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds <= 0
  ) {
    return clamp(targetSeconds);
  }

  // Whole beats only, computed rather than looped: the shift is at most one beat in practice (the
  // correction never exceeds half of one), but `ceil` costs the same as a compare and does not care
  // how far out the caller was.
  let shifted = targetSeconds;
  if (shifted < 0) {
    shifted += Math.ceil(-shifted / beatSeconds) * beatSeconds;
  } else if (shifted > durationSeconds) {
    shifted -= Math.ceil((shifted - durationSeconds) / beatSeconds) * beatSeconds;
  }

  // The shift can only fail by pushing past the *other* end, which means the track is shorter than
  // a beat. Nothing periodic helps there, so take the closest legal position.
  return shifted < 0 || shifted > durationSeconds ? clamp(targetSeconds) : shifted;
}

/**
 * The residual phase error between two decks, in seconds, as a magnitude.
 *
 * The same arithmetic as `phaseCorrectionSeconds` read as a measurement rather than an instruction —
 * which is the whole of AC-2's "±10 ms". Published from the audio thread every block so the number
 * that gets quoted is the one the audio thread saw, not one the UI reconstructed a frame later.
 */
export function phaseErrorSeconds(
  selfPositionSeconds: number,
  selfGrid: SyncGrid | null,
  otherPositionSeconds: number,
  otherGrid: SyncGrid | null,
): number {
  return Math.abs(
    phaseCorrectionSeconds(
      selfPositionSeconds,
      selfGrid,
      otherPositionSeconds,
      otherGrid,
    ),
  );
}

/**
 * The playback rate that makes this deck's effective tempo equal the other's.
 *
 * Effective tempo is the track's own tempo multiplied by how fast it is being played, so matching is
 * a ratio of beat *periods* — a longer beat needs a faster rate:
 *
 *     selfBeat / selfRate === otherBeat / otherRate
 *     selfRate === selfBeat * otherRate / otherBeat
 *
 * Periods rather than BPMs deliberately. `BeatGrid.beatSeconds` is "carried rather than derived from
 * the BPM, so a consumer cannot reintroduce a period error by rounding" (P3.5), and going back
 * through BPM here would do exactly that — at 128 BPM, a BPM rounded to one decimal is 3.4 ms of
 * grid error per minute.
 *
 * Returns `NaN` for an unusable input so the caller must decide; there is no safe default rate, and
 * silently returning 1 would look like a sync that worked.
 */
export function tempoMatchRate(
  selfGrid: SyncGrid | null,
  otherGrid: SyncGrid | null,
  otherRate: number,
): number {
  if (
    !isSyncableGrid(selfGrid) ||
    !isSyncableGrid(otherGrid) ||
    !Number.isFinite(otherRate)
  ) {
    return Number.NaN;
  }
  return (selfGrid.beatSeconds * otherRate) / otherGrid.beatSeconds;
}

/**
 * Ratios that count as a tempo match, for a deck asked to sync to a very different tempo.
 *
 * **Sync at the octave, because a DJ does.** 70 BPM against 140 is a match — one beat in two — and
 * refusing it would make sync useless across half a library. The multipliers are ordered so the
 * closest ratio to 1 wins, which is what "the smallest change to my deck" means.
 */
export const SYNC_OCTAVES = [1, 2, 0.5, 4, 0.25] as const;

/**
 * The tempo-match rate, folded to whichever octave needs the smallest rate change.
 *
 * Without this, syncing a 174 BPM drum-and-bass track to a 87 BPM hip-hop track would ask for rate
 * 0.5 — playing it at half speed rather than treating one beat as two. With it, the deck plays at
 * 1.0 and the grids line up every other beat, which is what the DJ meant.
 *
 * `maxRate` is the fader's reach. An octave that needs more than the fader has is skipped, so the
 * answer is always a rate the deck can actually be set to — or `NaN` when none of them is.
 */
export function bestTempoMatchRate(
  selfGrid: SyncGrid | null,
  otherGrid: SyncGrid | null,
  otherRate: number,
  maxRate: number,
): number {
  const base = tempoMatchRate(selfGrid, otherGrid, otherRate);
  if (!Number.isFinite(base)) {
    return Number.NaN;
  }

  let best = Number.NaN;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const octave of SYNC_OCTAVES) {
    const rate = base * octave;
    if (rate <= 0 || rate > maxRate) {
      continue;
    }
    // Distance from unchanged playback, not from the current rate: a DJ reads "how far off normal
    // speed is this track running", and that is what the pitch readout shows.
    const distance = Math.abs(Math.log2(rate));
    if (distance < bestDistance) {
      best = rate;
      bestDistance = distance;
    }
  }
  return best;
}
